import type { CollectedCollection, BasicCollectionInformation } from '@starly/starly-types';

const createdCollectionsComparator = (a: BasicCollectionInformation, b: BasicCollectionInformation) => {
  if (a.create_date.seconds > b.create_date.seconds) {
    return -1;
  }
  if (a.create_date.seconds < b.create_date.seconds) {
    return 1;
  }
  return 0;
};

const collectedCollectionsComparator = (a: CollectedCollection, b: CollectedCollection) => {
  if (!a.last_purchase_time) {
    return 1;
  }
  if (!b.last_purchase_time) {
    return -1;
  }

  if (a.last_purchase_time.seconds > b.last_purchase_time.seconds) {
    return -1;
  }
  if (a.last_purchase_time.seconds === b.last_purchase_time.seconds) {
    if (a.last_purchase_time.nanoseconds > b.last_purchase_time.nanoseconds) {
      return -1;
    }
  }
  return 1;
};

export const sortCollectedCollections = (collections?: { [collectionId: string]: CollectedCollection }) => {
  const arr = Object.values(collections || {});
  return arr.sort(collectedCollectionsComparator);
};

export const sortCreatedCollections = (collections?: { [collectionId: string]: BasicCollectionInformation }) => {
  const arr = Object.values(collections || {});
  return arr.sort(createdCollectionsComparator);
};

interface RarityById {
  [key: string]: { rarity: string, amount: number };
}

interface Rarities {
  [key: string]: { amount: number, lowestAmount: number };
}

export const getTags = (collection: CollectedCollection) => {
  const raritiesById: RarityById = Object.values(collection.collected_cards)
    .reduce((acc, v) => ({
      ...acc,
      [v.id]: {
        rarity: v.rarity,
        amount: acc[v.id] ? acc[v.id]?.amount + 1 : 1,
      },
    }), {});

  const rarities: Rarities = Object.values(raritiesById)
    .reduce((acc, rarity) => ({
      ...acc,
      [rarity.rarity]: {
        amount: acc[rarity.rarity]?.amount + 1,
        // eslint-disable-next-line
        lowestAmount: acc[rarity.rarity]?.lowestAmount === 0
          ? rarity?.amount : rarity.amount < acc[rarity.rarity]?.lowestAmount
            ? rarity?.amount : acc[rarity.rarity]?.lowestAmount,
      },
    }), {
      rare: { amount: 0, lowestAmount: 0 },
      legendary: { amount: 0, lowestAmount: 0 },
      common: { amount: 0, lowestAmount: 0 },
    });

  const distribution = collection?.card_information?.distribution;

  return ['legendary', 'rare', 'common']
    .map((rarity) => (!!rarities[rarity].amount
      && rarities[rarity].amount >= distribution[rarity]?.distinct_cards
      ? { rarity, number: rarities[rarity].lowestAmount } : null))
    .filter(<TValue>(value: TValue | null): value is TValue => value !== null);
};
